import UnAuthorizedLayout from "branchAndBrandSpecific/layout/UnAuthorizedLayout";
import { FC, useEffect, useState } from "react";
import "../styles/index.scss";
import { AuthCard } from "../components/AuthCard";
import useCloudFunctionCall from "@/hooks/useCloudFunctionCall";
import {
  AuthenticationProps,
  AuthenticationResponse,
} from "../types/authenticationTypes";
import { brand } from "branchAndBrandSpecific/utils/brandVariables/brandVariables";
import { createToast } from "@/utils/toaster/createToast";
import { useNavigate, useParams } from "react-router-dom";
import { FirebaseAuthMethods } from "../components/FirebaseAuthMethods";
import useDocumentTitle from "@/hooks/useDocumentTitle";
import i18next from "i18next";
import { authCredentialsAtom } from "@/utils/atoms/atomClient";
import { useSetAtom } from "jotai";
import {
  Box,
  Flex,
  Text,
  TextField,
  Button,
  Spinner,
  Separator,
} from "@radix-ui/themes";
import { getPasswordRules } from "../helperFunctions/getPasswordRules";
import PasswordRule from "../components/PasswordRule/PasswordRule";

export const Signup: FC<{}> = () => {
  const navigate = useNavigate();
  const { slug } = useParams();

  const [userCredentials, setUserCredentials] = useState<{
    email: string;
    password: string;
    verifyPassword: string;
  }>({
    email: slug ? decodeURI(slug) : "",
    password: "",
    verifyPassword: "",
  });

  const baseAuthenticationProps = {
    tenantId: brand.tenantId,
    method: "email",
    emailAddress: userCredentials.email,
    platform: brand.brandValue,
  } as AuthenticationProps;
  const setAtomCredentials = useSetAtom(authCredentialsAtom);

  //   Checks
  const isEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(userCredentials.email);
  const isPasswordValid =
    /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[@#$%^&+=!]).{8,}$/.test(
      userCredentials.password
    ); // Check if password contains every rule
  const passwordsMatch =
    userCredentials.password === userCredentials.verifyPassword;
  const passwordRules = getPasswordRules(userCredentials);
  //

  const setFieldValue = async (targetName: string, value: any) => {
    return setUserCredentials((prev: any) => ({
      ...prev,
      [targetName]: value,
    }));
  };

  // Auth logic
  const [response, _loading, _error, authenticate] =
    useCloudFunctionCall<AuthenticationResponse>(
      "userAuthentication",
      undefined
    );

  useEffect(() => {
    if (response && userCredentials.email) {
      if (response.status === "email-verification-sent") {
        setAtomCredentials({
          email: userCredentials.email,
          password: userCredentials.password,
        });
        localStorage.setItem("authEmailLocal", userCredentials.email);
        createToast({ type: "success", message: response.payload });
        return navigate(response.redirectURL);
      }

      if (
        response.status === "weak-password" ||
        response.status === "email-already-taken" ||
        response.status === "failed-to-create-user"
      ) {
        return createToast({ type: "error", message: response.payload });
      }
    }
  }, [response]);

  const handleNextStep = async () => {
    await authenticate({
      ...baseAuthenticationProps,
      action: "signup",
      password: userCredentials.password,
    });
  };

  // Page Document Title
  useDocumentTitle(i18next.t("auth.create.headline"));

  return (
    <UnAuthorizedLayout>
      <AuthCard title={i18next.t("auth.create.headline")} type="signup">
        <Flex direction="column" gap="4">
          {/* Email */}
          <Flex direction="column" gap="1">
            <Text as="label" size="2">
              {i18next.t("auth.input.email.label")}
            </Text>
            <TextField.Root
              placeholder={i18next.t("auth.input.email.placeholder")}
              size="3"
              type="text"
              value={userCredentials?.email}
              onChange={(e) => {
                setFieldValue("email", e.target.value);
              }}
              onKeyDown={async (e) => {
                if (e.key === "Enter") {
                  if (
                    isEmailValid &&
                    userCredentials.password &&
                    userCredentials.verifyPassword
                  ) {
                    handleNextStep();
                  }
                }
              }}
            />
          </Flex>

          {/* Password */}
          <Flex direction="column" gap="1">
            <Flex justify="between" align="end">
              <Text as="label" size="2">
                {i18next.t("auth.input.password.create.label")}
              </Text>
            </Flex>
            <TextField.Root
              placeholder={i18next.t("auth.input.password.create.placeholder")}
              size="3"
              type="password"
              value={userCredentials?.password}
              onChange={(e) => {
                setFieldValue("password", e.target.value);
              }}
              onKeyDown={async (e) => {
                if (e.key === "Enter") {
                  if (
                    isEmailValid &&
                    userCredentials.password &&
                    userCredentials.verifyPassword
                  ) {
                    handleNextStep();
                  }
                }
              }}
            />
          </Flex>

          {/* Veryify Password */}
          <Flex direction="column" gap="1">
            <Flex justify="between" align="end">
              <Text as="label" size="2">
                {i18next.t("auth.input.password.repeat.label")}
              </Text>
            </Flex>
            <TextField.Root
              placeholder={i18next.t("auth.input.password.repeat.placeholder")}
              size="3"
              type="password"
              value={userCredentials?.verifyPassword}
              onChange={(e) => {
                setFieldValue("verifyPassword", e.target.value);
              }}
              onKeyDown={async (e) => {
                if (e.key === "Enter") {
                  if (
                    isEmailValid &&
                    userCredentials.password &&
                    userCredentials.verifyPassword
                  ) {
                    handleNextStep();
                  }
                }
              }}
            />
          </Flex>

          <Flex wrap="wrap" gapY="2">
            {passwordRules.map((passRule, index) => (
              <PasswordRule
                key={`passRule_${index}`}
                rule={passRule.rule}
                isValid={passRule.isValid}
              />
            ))}
          </Flex>

          <Button
            disabled={!isEmailValid || !isPasswordValid || !passwordsMatch}
            variant="solid"
            size="3"
            onClick={async (e) => {
              handleNextStep();
            }}
          >
            {_loading ? <Spinner /> : i18next.t("auth.input.button.create")}
          </Button>

          {(brand.hasAuth.bnet ||
            brand.hasAuth.google ||
            brand.hasAuth.quickSignUp ||
            brand.hasAuth.discord ||
            brand.hasAuth.twitch ||
            brand.hasAuth.steam) && (
            <>
              <Box pt="3" pb="2">
                <Separator
                  className="SeparatorRoot"
                  decorative
                  style={{ width: "100%" }}
                />
              </Box>

              <FirebaseAuthMethods email={userCredentials.email} />
            </>
          )}
        </Flex>
      </AuthCard>
    </UnAuthorizedLayout>
  );
};
